import { isEmpty } from 'lodash';

export const validateRequired = (key: string) => {
  return (rowData: any) => {
    if (rowData?.tableData?.editing === 'delete') {
      return true; // No validation needed for deletions and save button has to be active
    }
    const newValue = rowData[key];

    if (isEmpty(rowData)) {
      return false; // No string validation needed for empty rows, but button will be disabled
    } else if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
      return 'Required'; // Input will have an error and button will be disabled
    }
    return true;
  };
};

export const validateUnique = (currentTableData: any[], key: string, required: boolean = false) => {
  return (rowData: any) => {
    if (rowData?.tableData?.editing === 'delete') {
      return true; // No validation needed for deletions and save button has to be active
    }

    const newValue = rowData[key];

    if (isEmpty(rowData)) {
      return false; // No string validation needed for empty rows, but button will be disabled
    } else if (required && (!newValue || (Array.isArray(newValue) && newValue.length === 0))) {
      return 'Required'; // Input will have an error and button will be disabled
    }

    if (newValue) {
      const existingRow = currentTableData.find((r) => {
        return (
          r[key] === newValue &&
          (typeof rowData.__rowId === 'undefined' || rowData.__rowId !== r.__rowId)
        );
      });

      if (existingRow) {
        return 'Value must be unique';
      }
    }

    return true;
  };
};

export const validateUniqueByCustomRule = <T>(
  currentTableData: T[],
  areRecordsEqual: (currentRecord: T, tableRecord: T) => boolean,
  errorMessage: string
) => {
  return (rowData: any) => {
    if (rowData?.tableData?.editing === 'delete') {
      return true; // No validation needed for deletions and save button has to be active
    }

    const existingRow = currentTableData.find((r) => {
      return (
        areRecordsEqual(rowData, r) &&
        (typeof rowData.__rowId === 'undefined' || rowData.__rowId !== (r as any).__rowId)
      );
    });

    if (existingRow) {
      return errorMessage;
    }

    return true;
  };
};

export const createHelperText = (errorMessage?: boolean | string) =>
  typeof errorMessage === 'boolean' || !errorMessage ? '\u00A0' : errorMessage;

const trimSpaces = (value: string): string => {
  if (value.startsWith(' ')) {
    return trimSpaces(value.slice(1, value.length));
  }
  if (value.endsWith(' ')) {
    return trimSpaces(value.slice(0, value.length - 1));
  }
  return value;
};

export const sanitize = (record: any): any => {
  if (Array.isArray(record)) {
    return record.map(sanitize);
  } else if (record === null) {
    return record;
  } else if (typeof record === 'object') {
    const sanitizedRecord: any = {};
    for (let key in record) {
      const item = sanitize(record[key]);
      if (item === '') continue;
      sanitizedRecord[key] = item;
    }
    return sanitizedRecord;
  } else if (typeof record === 'string') {
    return trimSpaces(record);
  } else {
    return record;
  }
};

export const validateScreamingSnakeCase = (
  key: string,
  requiredMessage: string,
  formatMessage: string
) => {
  return (rowData: any) => {
    if (rowData?.tableData?.editing === 'delete') {
      return true;
    }
    if (isEmpty(rowData)) {
      return false;
    }
    const value = rowData[key];
    if (!value) {
      return requiredMessage;
    }
    if (!/^[A-Z][A-Z0-9_]*$/.test(value)) {
      return formatMessage;
    }
    return true;
  };
};

export const composeValidators = (...validators: Array<(rowData: any) => boolean | string>) => {
  return (rowData: any) => {
    for (const validator of validators) {
      const result = validator(rowData);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };
};

export const alignCenter = {
  headerStyle: {
    paddingLeft: 26,
    paddingRight: 0,
    textAlign: 'center',
  },
  cellStyle: {
    textAlign: 'center',
  },
};
